import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  TablePagination
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import GroupIcon from "@mui/icons-material/Group";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HRCostCenters = () => {
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog / Modal state
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState("");

  useEffect(() => {
    fetchCostCenters();
  }, [page, limit]);

  const fetchCostCenters = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/cost-centers?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setCostCenters(response.data.data || []);
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.total);
        } else {
          setTotalCount(response.data.data.length);
        }
      }
    } catch (err) {
      //console.error("Error fetching cost centers:", err);
      setError(err.response?.data?.message || "Failed to load cost centers");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenView = async (cc) => {
    setSelectedCostCenter(cc);
    setOpenViewDialog(true);
    setEmployeesLoading(true);
    setEmployeesError("");
    setEmployees([]);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/cost-centers/${cc.id}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (err) {
      //console.error("Error fetching employees for cost center:", err);
      setEmployeesError(err.response?.data?.message || "Failed to load assigned employees");
    } finally {
      setEmployeesLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" >
          Cost Center Management
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      ) : costCenters.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No cost centers configured by the system administrator.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "grey.100" }}>
                <TableRow>
                  <TableCell><strong>Cost Center Name</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Agreement</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costCenters.map((cc) => (
                  <TableRow key={cc.id} hover>
                    <TableCell><strong>{cc.name}</strong></TableCell>
                    <TableCell>{cc.code}</TableCell>
                    <TableCell>{cc.department || "-"}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.85rem",
                          fontWeight: "500",
                          backgroundColor: cc.status === "Active" ? "#e8f5e9" : "#ffebee",
                          color: cc.status === "Active" ? "#2e7d32" : "#c62828",
                        }}
                      >
                        {cc.status}
                      </Box>
                    </TableCell>
                    <TableCell>{cc.description || "-"}</TableCell>
                    <TableCell>
                      {cc.agreementPath ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={cc.agreementPath.startsWith('http') ? cc.agreementPath : `${API_URL}/uploads/${cc.agreementPath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.25 }}
                        >
                          View
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No File
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{new Date(cc.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenView(cc)}
                        sx={{ textTransform: "none", borderRadius: 1.5 }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(1);
            }}
          />
        </Box>
      )}

      {/* Cost Center Details & Employee List Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Cost Center Details
            </Typography>
            <IconButton onClick={() => setOpenViewDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCostCenter && (
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCostCenter.name}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Code
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCostCenter.code}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedCostCenter.status}
                    color={selectedCostCenter.status === "Active" ? "success" : "default"}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {selectedCostCenter.department || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {selectedCostCenter.description || "No description provided."}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "flex-end" }}>
                  {selectedCostCenter.agreementPath && (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<GetAppIcon />}
                      href={selectedCostCenter.agreementPath.startsWith('http') ? selectedCostCenter.agreementPath : `${API_URL}/uploads/${selectedCostCenter.agreementPath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none", mt: 1 }}
                    >
                      Agreement Document
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <GroupIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" fontWeight="bold">
                Assigned Employees
              </Typography>
            </Box>

            {employeesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : employeesError ? (
              <Alert severity="error">{employeesError}</Alert>
            ) : employees.length === 0 ? (
              <Paper elevation={0} sx={{ p: 3, textAlign: "center", backgroundColor: "grey.50", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No employees are currently assigned to this cost center.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "grey.50" }}>
                    <TableRow>
                      <TableCell><strong>Employee ID</strong></TableCell>
                      <TableCell><strong>Full Name</strong></TableCell>
                      <TableCell><strong>Department</strong></TableCell>
                      <TableCell><strong>Designation</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.employee_id} hover>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell>
                          <strong>{emp.firstName} {emp.lastName}</strong>
                        </TableCell>
                        <TableCell>{emp.department || "-"}</TableCell>
                        <TableCell>{emp.designation}</TableCell>
                        <TableCell>{emp.email || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={emp.employmentStatus}
                            color={
                              emp.employmentStatus.toLowerCase() === "active"
                                ? "success"
                                : emp.employmentStatus.toLowerCase() === "terminated"
                                ? "error"
                                : "default"
                            }
                            variant="outlined"
                            sx={{ fontSize: "0.75rem", height: 20 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenViewDialog(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRCostCenters;
